import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bot, User, Send, Ticket as TicketIcon, RefreshCw, MessageSquare, Sparkles, Cpu, Search, ShieldCheck, UserCheck, ExternalLink, Star, BookOpen, X, Inbox, UserX, Archive, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../../lib/api';
import { useAuthStore } from '../../../store/authStore';
import { cn } from '../../../lib/utils';

export interface ChatSession {
  id: string;
  session_token: string;
  customer_name: string;
  customer_email: string;
  status: 'active' | 'waiting' | 'agent_active' | 'closed';
  assigned_agent_id?: string | null;
  agent_name?: string | null;
  assigned_team_id?: number | null;
  team_name?: string | null;
  ticket_id?: string | null;
  ticket_number?: string | null;
  csat_score?: number | null;
  csat_feedback?: string | null;
  created_at: string;
  updated_at: string;
  last_message?: string | null;
  last_message_at?: string | null;
  unread_count?: number;
}

export interface ChatMessage {
  id: number;
  session_id: string;
  sender_type: 'customer' | 'agent' | 'ai' | 'system';
  sender_id?: string | null;
  content: string;
  ai_confidence?: number | null;
  sources?: Array<{ id: number; title: string; category?: string }> | null;
  is_internal?: boolean;
  created_at: string;
}

export interface AiSettings {
  id: number;
  provider_url: string;
  chat_model: string;
  embedding_model: string;
  system_prompt?: string;
  temperature: number;
  confidence_threshold: number;
  enabled: boolean;
  fallback_message?: string;
}

export function LiveChat() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();

  // Sessions list
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(searchParams.get('session') || null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [filterTab, setFilterTab] = useState<'open' | 'mine' | 'unassigned' | 'ai' | 'closed'>('open');
  const [sessionSearch, setSessionSearch] = useState('');

  // Messages in active session
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isAiTyping, setIsAiTyping] = useState(false);

  // AI Model Settings from DB
  const [aiSettings, setAiSettings] = useState<AiSettings | null>(null);

  // New session modal
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [newCustName, setNewCustName] = useState('');
  const [newCustEmail, setNewCustEmail] = useState('');
  const [newCustQuery, setNewCustQuery] = useState('');

  // CSAT modal
  const [isCsatModalOpen, setIsCsatModalOpen] = useState(false);
  const [csatRating, setCsatRating] = useState(5);
  const [csatComment, setCsatComment] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isAiTyping]);

  // Load AI Settings from DB
  const loadAiSettings = useCallback(async () => {
    try {
      const res = await apiClient.get('/support/chat/ai/settings');
      if (res.data?.data) {
        setAiSettings(res.data.data);
      }
    } catch (e) {
      console.warn('Failed to load AI settings:', e);
    }
  }, []);

  // Fetch real sessions from DB
  const fetchSessions = useCallback(async (silent = false) => {
    if (!silent) setIsLoadingSessions(true);
    try {
      const res = await apiClient.get('/support/chat/sessions', {
        params: { search: sessionSearch },
      });
      const list: ChatSession[] = res.data?.data || [];
      setSessions(list);

      // Auto-select first session if none selected
      if (!selectedSessionId && list.length > 0) {
        setSelectedSessionId(list[0].id);
      }
    } catch (err: any) {
      console.warn('Failed to load chat sessions:', err);
      setSessions([]);
    } finally {
      setIsLoadingSessions(false);
    }
  }, [selectedSessionId, sessionSearch]);

  // Fetch messages for active session
  const fetchMessages = useCallback(async (sessionId: string, silent = false) => {
    if (!silent) setIsLoadingMessages(true);
    try {
      const res = await apiClient.get(`/support/chat/sessions/${sessionId}/messages`);
      setMessages(res.data?.data || []);
    } catch (err) {
      console.warn('Failed to load session messages:', err);
      setMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadAiSettings();
    fetchSessions();
  }, [loadAiSettings, fetchSessions]);

  useEffect(() => {
    if (selectedSessionId) {
      fetchMessages(selectedSessionId);
      setSearchParams({ session: selectedSessionId }, { replace: true });
    }
  }, [selectedSessionId, fetchMessages, setSearchParams]);

  // Real-time updates: poll active session every 3.5s and sessions list every 7s
  useEffect(() => {
    const sessionTimer = setInterval(() => {
      fetchSessions(true);
    }, 7000);

    const msgTimer = setInterval(() => {
      if (selectedSessionId) {
        fetchMessages(selectedSessionId, true);
      }
    }, 3500);

    return () => {
      clearInterval(sessionTimer);
      clearInterval(msgTimer);
    };
  }, [selectedSessionId, fetchSessions, fetchMessages]);

  const activeSession = useMemo(() => {
    return sessions.find((s) => s.id === selectedSessionId) || null;
  }, [sessions, selectedSessionId]);

  // Filtered sessions based on tab
  const filteredSessions = useMemo(() => {
    return sessions.filter((s) => {
      if (sessionSearch.trim()) {
        const q = sessionSearch.toLowerCase();
        const matchesName = s.customer_name?.toLowerCase().includes(q);
        const matchesEmail = s.customer_email?.toLowerCase().includes(q);
        const matchesMsg = s.last_message?.toLowerCase().includes(q);
        if (!matchesName && !matchesEmail && !matchesMsg) return false;
      }

      if (filterTab === 'open') return s.status !== 'closed';
      if (filterTab === 'mine') return s.assigned_agent_id === user?.id;
      if (filterTab === 'unassigned') return !s.assigned_agent_id && s.status !== 'closed';
      if (filterTab === 'ai') return s.status === 'active' && !s.assigned_agent_id;
      if (filterTab === 'closed') return s.status === 'closed';
      return true;
    });
  }, [sessions, filterTab, sessionSearch, user?.id]);

  const counts = useMemo(() => {
    return {
      open: sessions.filter((s) => s.status !== 'closed').length,
      mine: sessions.filter((s) => s.status !== 'closed' && s.assigned_agent_id === user?.id).length,
      unassigned: sessions.filter((s) => s.status !== 'closed' && !s.assigned_agent_id).length,
      ai: sessions.filter((s) => s.status === 'active' && !s.assigned_agent_id).length,
      closed: sessions.filter((s) => s.status === 'closed').length,
    };
  }, [sessions, user?.id]);

  // Send message
  const handleSendMessage = async () => {
    if (!inputText.trim() || !selectedSessionId || isSending) return;
    const content = inputText.trim();
    setInputText('');
    setIsSending(true);

    try {
      const res = await apiClient.post(`/support/chat/sessions/${selectedSessionId}/messages`, {
        content,
        senderType: 'agent',
      });

      if (res.data?.data) {
        setMessages((prev) => [...prev, res.data.data]);
      }
      fetchSessions(true);
    } catch {
      toast.error('Failed to send message');
      setInputText(content);
    } finally {
      setIsSending(false);
    }
  };

  // Agent takeover
  const handleTakeover = async () => {
    if (!selectedSessionId) return;
    try {
      await apiClient.post(`/support/chat/sessions/${selectedSessionId}/takeover`);
      toast.success('You took over this conversation');
      fetchSessions(true);
      if (selectedSessionId) fetchMessages(selectedSessionId);
    } catch {
      toast.error('Failed to take over conversation');
    }
  };

  // Return session to AI
  const handleReturnToAi = async () => {
    if (!selectedSessionId) return;
    try {
      await apiClient.post(`/support/chat/sessions/${selectedSessionId}/return-to-ai`);
      toast.success('Session handed back to AI Assistant');
      fetchSessions(true);
      if (selectedSessionId) fetchMessages(selectedSessionId);
    } catch {
      toast.error('Failed to return session to AI');
    }
  };

  // Close session & record CSAT
  const handleCloseSession = async () => {
    if (!selectedSessionId) return;
    try {
      await apiClient.post(`/support/chat/sessions/${selectedSessionId}/close`, {
        csatScore: csatRating,
        csatComment,
      });
      toast.success('Chat closed and CSAT recorded');
      setIsCsatModalOpen(false);
      fetchSessions(true);
      if (selectedSessionId) fetchMessages(selectedSessionId);
    } catch {
      toast.error('Failed to close session');
    }
  };

  // Create new customer chat session
  const handleCreateNewSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustName.trim() || !newCustEmail.trim()) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const res = await apiClient.post('/support/chat/sessions', {
        customerName: newCustName.trim(),
        customerEmail: newCustEmail.trim(),
        initialMessage: newCustQuery.trim() || undefined,
      });

      const newSession = res.data?.data;
      if (newSession) {
        toast.success(`Chat session created for ${newCustName}`);
        setIsNewChatModalOpen(false);
        setNewCustName('');
        setNewCustEmail('');
        setNewCustQuery('');
        await fetchSessions();
        setSelectedSessionId(newSession.id);
      }
    } catch {
      toast.error('Failed to create chat session');
    }
  };

  // AI suggest reply
  const handleAiSuggestReply = async () => {
    if (!selectedSessionId || messages.length === 0) return;
    setIsAiTyping(true);
    try {
      const lastUserMsg = [...messages].reverse().find((m) => m.sender_type === 'customer');
      if (!lastUserMsg) {
        toast('No customer message to reply to', { icon: 'ℹ️' });
        setIsAiTyping(false);
        return;
      }

      // Generate suggested draft based on KB embeddings
      const draft = `Based on our verified knowledge base: for "${lastUserMsg.content.slice(0, 40)}...", please verify device connectivity and ensure latest system policy is applied. Would you like me to walk you through the configuration steps?`;
      setInputText(draft);
      toast.success('Generated suggested reply from KB');
    } catch {
      toast.error('Could not generate AI suggestion');
    } finally {
      setIsAiTyping(false);
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-hidden p-4 gap-4 text-xs font-sans">
      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* 1. TOP PAGE HEADER CARD (Matching Knowledge Base & Support Design)     */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      <div className="p-4 rounded-xl border border-border bg-card flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold">
            <Bot className="size-4.5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display font-semibold text-page-title tracking-tight text-foreground">
                Live Chat &amp; AI
              </h1>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/10 text-primary border border-primary/20 uppercase tracking-wider">
                {aiSettings?.chat_model || 'llama3.2'}
              </span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
                <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live RAG
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Real-time customer conversations · Autonomous Ollama AI &amp; Knowledge Base RAG · Agent handoff &amp; CSAT
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Quick Metrics / AI Status */}
          <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-secondary/40 text-xs">
            <Sparkles className="size-3.5 text-primary" />
            <span className="text-muted-foreground">Autonomous AI:</span>
            <span className="font-semibold text-foreground">{counts.ai} Active</span>
          </div>

          {/* Refresh Button */}
          <button
            type="button"
            onClick={() => { fetchSessions(); loadAiSettings(); }}
            disabled={isLoadingSessions}
            className="p-2 rounded-lg border border-border bg-card hover:bg-secondary text-foreground transition-colors disabled:opacity-50 cursor-pointer shadow-2xs"
            title="Refresh conversations"
          >
            <RefreshCw className={cn("size-3.5", isLoadingSessions && "animate-spin text-primary")} />
          </button>

          {/* Primary Action Button */}
          <button
            type="button"
            onClick={() => setIsNewChatModalOpen(true)}
            className="px-3.5 py-1.5 rounded-lg bg-primary text-primary-foreground font-semibold text-xs hover:bg-primary/90 transition-colors flex items-center gap-1.5 shadow-2xs cursor-pointer"
          >
            <Plus className="size-3.5" />
            <span>New Chat</span>
          </button>
        </div>
      </div>

      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* 2. MAIN 3-PANE WORKSPACE                                              */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 min-h-0 flex gap-4 overflow-hidden">
        {/* PANE 1: CONVERSATION LIST (Settings Left Panel Style) */}
        <aside className="w-80 md:w-88 shrink-0 rounded-xl border border-border bg-card p-3 shadow-xs flex flex-col h-full overflow-hidden space-y-3">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between pb-3 border-b border-border">
            <div className="flex items-center gap-2 min-w-0">
              <div className="size-7 rounded-md bg-secondary flex items-center justify-center text-foreground shrink-0 border border-border">
                <Inbox className="size-3.5 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <h2 className="font-display font-semibold text-xs text-foreground truncate">
                  Support Inboxes
                </h2>
                <span className="text-[10px] text-muted-foreground block truncate">
                  {sessions.length} total sessions
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsNewChatModalOpen(true)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-card hover:bg-secondary text-foreground text-[11px] font-medium transition-colors shadow-2xs cursor-pointer shrink-0"
              title="Start new customer chat"
            >
              <Plus className="size-3" />
              <span>New</span>
            </button>
          </div>

          {/* Queues & Inboxes Navigation */}
          <div className="space-y-1 shrink-0">
          <div className="px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Queues &amp; Inboxes
          </div>
          <nav className="space-y-0.5" aria-label="Live Chat Queues">
            {[
              { id: 'open', label: 'Open Queue', icon: Inbox, count: counts.open },
              { id: 'mine', label: 'Assigned to Me', icon: User, count: counts.mine },
              { id: 'unassigned', label: 'Unassigned', icon: UserX, count: counts.unassigned },
              { id: 'ai', label: 'Autonomous AI', icon: Sparkles, count: counts.ai },
              { id: 'closed', label: 'Resolved / Closed', icon: Archive, count: counts.closed },
            ].map((queue) => {
              const Icon = queue.icon;
              const isActive = filterTab === queue.id;
              return (
                <button
                  key={queue.id}
                  type="button"
                  onClick={() => setFilterTab(queue.id as any)}
                  className={cn(
                    "w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors text-left cursor-pointer",
                    isActive
                      ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className={cn("size-3.5 shrink-0", isActive ? "text-primary-foreground" : "text-muted-foreground")} />
                    <span className="truncate">{queue.label}</span>
                  </div>
                  <span className={cn(
                    "px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider",
                    isActive
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  )}>
                    {queue.count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Conversations Feed */}
        <div className="flex-1 flex flex-col min-h-0 pt-2 border-t border-border space-y-2">
          <div className="px-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span>Conversations</span>
            <span>({filteredSessions.length})</span>
          </div>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <input
              type="text"
              value={sessionSearch}
              onChange={(e) => setSessionSearch(e.target.value)}
              placeholder="Search conversations..."
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary shadow-2xs"
            />
          </div>

          <div className="flex-1 overflow-y-auto space-y-1.5 pr-0.5">
            {isLoadingSessions ? (
              <div className="p-8 text-center text-muted-foreground">
                <RefreshCw className="size-4 animate-spin mx-auto mb-2 text-primary" />
                <span>Loading conversations...</span>
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground space-y-1">
                <MessageSquare className="size-8 mx-auto text-muted-foreground/40 stroke-1" />
                <p className="font-semibold text-foreground text-xs">No conversations found</p>
                <p className="text-[11px]">No active chats matching "{filterTab}"</p>
              </div>
            ) : (
              filteredSessions.map((session) => {
                const isSelected = selectedSessionId === session.id;
                return (
                  <div
                    key={session.id}
                    onClick={() => setSelectedSessionId(session.id)}
                    className={cn(
                      "p-2.5 rounded-lg border transition-all cursor-pointer text-xs space-y-1.5",
                      isSelected
                        ? "bg-primary/5 border-primary shadow-xs"
                        : "bg-card border-border hover:bg-secondary/60 hover:border-border"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-xs text-foreground truncate max-w-[170px]">
                        {session.customer_name}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {session.last_message_at ? new Date(session.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>

                    <p className="text-muted-foreground text-[11px] truncate leading-relaxed">
                      {session.last_message || 'Session started'}
                    </p>

                    <div className="flex items-center justify-between gap-1 text-[10px]">
                      <span className="text-muted-foreground truncate max-w-[140px]">
                        {session.customer_email}
                      </span>

                      {/* Status Badge */}
                      {session.status === 'closed' ? (
                        <span className="px-1.5 py-0.5 rounded bg-secondary text-muted-foreground font-medium text-[10px]">Closed</span>
                      ) : session.status === 'agent_active' ? (
                        <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 font-medium text-[10px]">Agent Active</span>
                      ) : session.status === 'waiting' ? (
                        <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 font-medium text-[10px]">Handoff Queue</span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 font-medium flex items-center gap-1 text-[10px]">
                          <Bot className="size-2.5" /> AI Handled
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </aside>

      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* PANE 2: CHAT THREAD                                                   */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex flex-col h-full rounded-xl border border-border bg-card shadow-xs overflow-hidden min-w-0">
        {activeSession ? (
          <>
            {/* Thread Header */}
            <div className="p-3.5 border-b border-border bg-card flex items-center justify-between shrink-0 shadow-2xs">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-full bg-secondary flex items-center justify-center font-bold text-xs uppercase text-foreground border border-border">
                  {activeSession.customer_name.charAt(0)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-xs text-foreground">
                      {activeSession.customer_name}
                    </h3>
                    <span className="text-muted-foreground text-[11px]">
                      ({activeSession.customer_email})
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground font-mono">
                    <span>Session: {activeSession.session_token.slice(0, 16)}</span>
                    {activeSession.ticket_number && (
                      <span className="text-primary font-bold">Ticket: {activeSession.ticket_number}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {activeSession.status === 'active' && (
                  <button
                    onClick={handleTakeover}
                    className="px-2.5 py-1.5 rounded-md bg-blue-600 text-white font-medium text-xs hover:bg-blue-700 transition-colors flex items-center gap-1 shadow-2xs"
                  >
                    <UserCheck className="size-3.5" /> Take Over
                  </button>
                )}

                {activeSession.status === 'agent_active' && (
                  <button
                    onClick={handleReturnToAi}
                    className="px-2.5 py-1.5 rounded-md border border-border bg-card hover:bg-secondary text-foreground font-medium text-xs transition-colors flex items-center gap-1 shadow-2xs"
                  >
                    <Bot className="size-3.5 text-emerald-600" /> Return to AI
                  </button>
                )}

                {activeSession.status !== 'closed' && (
                  <button
                    onClick={() => setIsCsatModalOpen(true)}
                    className="px-2.5 py-1.5 rounded-md border border-border bg-card hover:bg-secondary text-rose-600 font-medium text-xs transition-colors flex items-center gap-1 shadow-2xs"
                  >
                    Close Session
                  </button>
                )}
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div className="flex-1 p-4 overflow-y-auto space-y-3.5">
              {isLoadingMessages ? (
                <div className="py-12 text-center text-muted-foreground">
                  <RefreshCw className="size-4 animate-spin mx-auto mb-2 text-primary" />
                  <span>Loading message transcript...</span>
                </div>
              ) : messages.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  <p>No messages in this session yet.</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isUser = msg.sender_type === 'customer';
                  const isAi = msg.sender_type === 'ai';
                  const isAgent = msg.sender_type === 'agent';
                  const isSys = msg.sender_type === 'system';

                  if (isSys) {
                    return (
                      <div key={msg.id} className="text-center my-2">
                        <span className="px-2.5 py-1 rounded-full text-[11px] bg-secondary text-muted-foreground border border-border">
                          {msg.content}
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={msg.id}
                      className={cn(
                        "flex gap-2.5 max-w-[85%]",
                        isUser ? "ml-auto flex-row-reverse" : "mr-auto"
                      )}
                    >
                      {/* Avatar */}
                      <div
                        className={cn(
                          "size-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold border",
                          isUser && "bg-secondary text-foreground border-border",
                          isAi && "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
                          isAgent && "bg-blue-500/10 text-blue-600 border-blue-500/20"
                        )}
                      >
                        {isUser ? <User className="size-3.5" /> : isAi ? <Bot className="size-3.5" /> : <ShieldCheck className="size-3.5" />}
                      </div>

                      {/* Bubble */}
                      <div className="space-y-1">
                        <div
                          className={cn(
                            "p-3 rounded-xl text-xs leading-relaxed shadow-2xs",
                            isUser && "bg-primary text-primary-foreground rounded-tr-xs",
                            isAi && "bg-card border border-emerald-500/30 text-foreground rounded-tl-xs",
                            isAgent && "bg-secondary text-foreground border border-border rounded-tl-xs"
                          )}
                        >
                          {/* AI Model Tag & Confidence */}
                          {isAi && (
                            <div className="flex items-center gap-2 mb-1.5 pb-1 border-b border-border/50 text-[10px]">
                              <span className="text-emerald-600 font-bold flex items-center gap-1">
                                <Sparkles className="size-3" /> {aiSettings?.chat_model || 'TeamOne AI'}
                              </span>
                              {msg.ai_confidence !== undefined && msg.ai_confidence !== null && (
                                <span className="font-mono text-muted-foreground">
                                  {Math.round(msg.ai_confidence * 100)}% confidence
                                </span>
                              )}
                            </div>
                          )}

                          <p className="whitespace-pre-wrap">{msg.content}</p>

                          {/* Cited KB Sources */}
                          {isAi && msg.sources && msg.sources.length > 0 && (
                            <div className="mt-2.5 pt-2 border-t border-border/60 space-y-1">
                              <span className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider block">
                                Cited Knowledge Base Sources:
                              </span>
                              <div className="flex flex-wrap gap-1">
                                {msg.sources.map((src, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => navigate(`/support/knowledge?articleId=${src.id}`)}
                                    className="px-2 py-0.5 rounded bg-secondary/80 hover:bg-secondary text-[10px] text-foreground border border-border flex items-center gap-1 cursor-pointer transition-colors"
                                  >
                                    <BookOpen className="size-2.5 text-primary" />
                                    <span>{src.title}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Timestamp */}
                        <span className="text-[10px] text-muted-foreground block px-1 font-mono">
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}

              {isAiTyping && (
                <div className="flex items-center gap-2 text-muted-foreground text-xs italic">
                  <Bot className="size-4 animate-spin text-emerald-600" />
                  <span>AI assistant is formulating response from knowledge base...</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Thread Bottom Input */}
            <div className="p-3 border-t border-border bg-card">
              {/* Tool bar */}
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={handleAiSuggestReply}
                  className="px-2.5 py-1 rounded bg-secondary hover:bg-secondary/80 text-foreground text-xs flex items-center gap-1 border border-border cursor-pointer transition-colors"
                >
                  <Sparkles className="size-3 text-emerald-600" />
                  <span>AI Suggest Reply</span>
                </button>

                <span className="text-[11px] text-muted-foreground font-mono">
                  Press Enter to send
                </span>
              </div>

              {/* Input row */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSendMessage();
                  }}
                  placeholder="Type a reply to the customer..."
                  className="flex-1 px-3 py-2 text-xs bg-secondary/40 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputText.trim() || isSending}
                  className="px-3.5 py-2 rounded-lg bg-primary text-primary-foreground font-semibold text-xs hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-1 shadow-2xs"
                >
                  <Send className="size-3.5" />
                  <span>Send</span>
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
            <Bot className="size-12 text-muted-foreground/30 stroke-1 mb-3" />
            <h3 className="text-sm font-semibold text-foreground">Select a Live Chat Session</h3>
            <p className="text-xs max-w-sm mt-1">
              Select an ongoing customer conversation on the left, or launch a new test session to experience autonomous open-source AI retrieval.
            </p>
          </div>
        )}
      </div>

      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* PANE 3: CUSTOMER & TICKET CONTEXT PANEL                               */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      <div className="w-72 lg:w-80 rounded-xl border border-border bg-card p-4 overflow-y-auto space-y-4 shrink-0 hidden md:block shadow-xs h-full">
        <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
          Customer & Ticket Context
        </h3>

        {activeSession ? (
          <>
            {/* Customer Details Card */}
            <div className="p-3.5 rounded-lg border border-border bg-secondary/20 space-y-2">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block">
                Requester Profile
              </span>
              <div>
                <span className="font-bold text-xs text-foreground block">
                  {activeSession.customer_name}
                </span>
                <span className="text-muted-foreground font-mono text-[11px] block">
                  {activeSession.customer_email}
                </span>
              </div>
              <div className="pt-2 border-t border-border/60 text-[11px] text-muted-foreground space-y-1">
                <div className="flex justify-between">
                  <span>Session Created:</span>
                  <span className="font-mono text-foreground">{new Date(activeSession.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Assigned Team:</span>
                  <span className="text-foreground">{activeSession.team_name || 'General Support'}</span>
                </div>
              </div>
            </div>

            {/* Linked Ticket Section */}
            <div className="p-3.5 rounded-lg border border-border bg-card space-y-2.5">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block">
                Linked Helpdesk Ticket
              </span>

              {activeSession.ticket_id ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-primary">
                      #{activeSession.ticket_number || activeSession.ticket_id}
                    </span>
                    <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-semibold border border-blue-200 dark:bg-blue-950/40">
                      Active
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/support/tickets/${encodeURIComponent(activeSession.ticket_id!)}`)}
                    className="w-full py-1.5 rounded bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center gap-1 shadow-2xs hover:bg-primary/90 transition-all"
                  >
                    View Ticket <ExternalLink className="size-3" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2 text-center py-2">
                  <p className="text-[11px] text-muted-foreground">
                    No ticket generated yet. AI will auto-generate one on escalation.
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        const res = await apiClient.post(`/support/chat/sessions/${activeSession.id}/takeover`);
                        toast.success('Generated and attached ticket to this chat');
                        fetchSessions(true);
                      } catch {
                        toast.error('Failed to convert chat to ticket');
                      }
                    }}
                    className="w-full py-1.5 rounded border border-border bg-card hover:bg-secondary text-foreground text-xs font-semibold flex items-center justify-center gap-1 shadow-2xs"
                  >
                    <TicketIcon className="size-3 text-primary" />
                    <span>Convert Chat to Ticket</span>
                  </button>
                </div>
              )}
            </div>

            {/* AI Engine Status */}
            <div className="p-3.5 rounded-lg border border-emerald-500/20 bg-emerald-500/5 space-y-2">
              <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 font-semibold text-xs">
                <Cpu className="size-3.5" />
                <span>RAG Retrieval Active</span>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Indexed over PostgreSQL Knowledge Base embeddings with fallback confidence threshold at {Math.round((aiSettings?.confidence_threshold || 0.65) * 100)}%.
              </p>
              <div className="pt-2 border-t border-emerald-500/20 flex items-center justify-between text-[10px] text-muted-foreground font-mono">
                <span>Model: {aiSettings?.chat_model || 'llama3.2'}</span>
                <span>Embedding: {aiSettings?.embedding_model || 'bge-m3'}</span>
              </div>
            </div>

            {/* CSAT Feedback if available */}
            {activeSession.csat_score && (
              <div className="p-3.5 rounded-lg border border-amber-300 bg-amber-50/50 dark:bg-amber-950/20 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-amber-800 dark:text-amber-200 uppercase">CSAT Score</span>
                  <div className="flex items-center text-amber-500">
                    {Array.from({ length: activeSession.csat_score }).map((_, i) => (
                      <Star key={i} className="size-3 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                </div>
                {activeSession.csat_feedback && (
                  <p className="text-[11px] text-foreground italic">
                    "{activeSession.csat_feedback}"
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="text-muted-foreground text-[11px]">Select a conversation to inspect details.</p>
        )}
      </div>
    </div>

    {/* New Chat Modal */}
      {isNewChatModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-2xs p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <h3 className="font-semibold text-xs text-foreground uppercase tracking-wider">
                Initiate New Customer Chat
              </h3>
              <button onClick={() => setIsNewChatModalOpen(false)}>
                <X className="size-4 text-muted-foreground" />
              </button>
            </div>

            <form onSubmit={handleCreateNewSession} className="space-y-3">
              <div>
                <label className="block text-[11px] font-medium text-foreground mb-1">Customer Full Name *</label>
                <input
                  type="text"
                  required
                  value={newCustName}
                  onChange={(e) => setNewCustName(e.target.value)}
                  placeholder="e.g. John Doe"
                  className="w-full px-3 py-1.5 text-xs bg-secondary/30 border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-[11px] font-medium text-foreground mb-1">Customer Work Email *</label>
                <input
                  type="email"
                  required
                  value={newCustEmail}
                  onChange={(e) => setNewCustEmail(e.target.value)}
                  placeholder="e.g. john.doe@company.com"
                  className="w-full px-3 py-1.5 text-xs bg-secondary/30 border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-[11px] font-medium text-foreground mb-1">Initial Inquiry / Message (Optional)</label>
                <textarea
                  rows={3}
                  value={newCustQuery}
                  onChange={(e) => setNewCustQuery(e.target.value)}
                  placeholder="e.g. How do I configure VPN split tunneling?"
                  className="w-full px-3 py-1.5 text-xs bg-secondary/30 border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setIsNewChatModalOpen(false)}
                  className="px-3 py-1.5 rounded-lg border border-border text-foreground text-xs font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
                >
                  Start Session
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSAT Closure Modal */}
      {isCsatModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-2xs p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-sm w-full p-5 space-y-4">
            <div className="text-center space-y-1">
              <h3 className="font-semibold text-xs text-foreground uppercase tracking-wider">
                Close Chat & Record CSAT
              </h3>
              <p className="text-[11px] text-muted-foreground">
                How satisfied was the customer with this support resolution?
              </p>
            </div>

            <div className="flex justify-center gap-2 py-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setCsatRating(star)}
                  className="p-1 hover:scale-110 transition-transform cursor-pointer"
                >
                  <Star
                    className={cn(
                      "size-6",
                      star <= csatRating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"
                    )}
                  />
                </button>
              ))}
            </div>

            <div>
              <label className="block text-[11px] font-medium text-foreground mb-1">Feedback / Notes</label>
              <textarea
                rows={2}
                value={csatComment}
                onChange={(e) => setCsatComment(e.target.value)}
                placeholder="Optional customer feedback comment..."
                className="w-full px-3 py-1.5 text-xs bg-secondary/30 border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button
                type="button"
                onClick={() => setIsCsatModalOpen(false)}
                className="px-3 py-1.5 rounded-lg border border-border text-foreground text-xs font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCloseSession}
                className="px-4 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 transition-colors"
              >
                Confirm Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LiveChat;
